import { Injectable, NgZone } from '@angular/core';
import { BranchDeepLinks, BranchInitEvent } from 'capacitor-branch-deep-links';
import { Browser } from '@capacitor/browser';
import { ToastrService } from 'ngx-toastr';
import { environment } from '../../../../../../apps/ion-caballus/src/environments/environment';
import { AuthService } from './auth.service';
import { NetworkStatusService } from './network-status.service';
import { Capacitor } from '@capacitor/core';
import { Router } from '@angular/router';

declare global {
    interface Window {
        branch: any;
    }
}

export interface BranchLinkData {
    url: string;
    appRoute: string;
    feature?: string;
    title: string;
    description: string;
    imageUrl?: string;
    canonicalUrl?: string;
    isHorseProfileRoute?: boolean;
}

@Injectable({
    providedIn: 'root'
})
export class BranchService {
    private branchInitialized = false;
    private initializationPromise: Promise<any> | null = null;

    constructor(
        private readonly _authService: AuthService,
        private readonly _toastService: ToastrService,
        private readonly _networkStatusService: NetworkStatusService,
        private readonly _ngZone: NgZone,
        private readonly _router: Router
    ) {
        this._listenToBranchLinks();
    }

    private async _listenToBranchLinks() {
        if (Capacitor.isNativePlatform()) {
            // BranchIo event listener
            BranchDeepLinks.addListener('init', (event: BranchInitEvent) => {
                // Retrieve deeplink keys from 'referringParams' and evaluate the values to determine where to route the user
                // Check '+clicked_branch_link' before deciding whether to use your Branch routing logic
                // verify if the branch link is clicked
                if (event.referringParams['+clicked_branch_link']) {
                    // fetch url from branch link
                    const urlToNavigate =
                        event.referringParams.appendUrl || event.referringParams.custom_string;

                    // custom_bool is used to determine if the link is a horse profile link
                    if (event.referringParams.custom_bool) {
                        // open Horse Profile PWA app
                        Browser.open({
                            url: event.referringParams['$desktop_url']
                        });
                    } else {
                        // During initialization, we need to ensure the navigation happens after Angular is ready
                        this._ngZone.run(() => {
                            this._router.navigateByUrl(urlToNavigate, { replaceUrl: true });
                        });
                    }
                }
            });

            // branch io error listener
            BranchDeepLinks.addListener('initError', (error: any) => {
                console.error('Branch Deep Links init error:', error);
            });
        }
    }

    /**
     * Go to my account page
     */
    public async goToMyAccountPWAPage() {
        // check network status
        const networkStatus = await this._networkStatusService.networkStatus$.toPromise();
        if (networkStatus.isOnline) {
            // fire subscription token generation api
            const tokenResponse = await this._authService
                .generateAccountSubscriptionToken()
                .toPromise();
            if (tokenResponse.token) {
                const deeplink = await this.createLinkWithOpenGraph({
                    url: `${environment.ionBaseUrl}/tabs/map-my-ride`,
                    appRoute: '/tabs/map-my-ride',
                    title: 'Subscription App',
                    description: 'Subscription App',
                    feature: 'subscriptionApp'
                });
                // open PWA app
                Browser.open({
                    url: `${environment.ionHorseProfileUrl}?token=${tokenResponse.token}&deepLink=${deeplink}`
                });
            } else {
                // throw error when token generation fails
                this._toastService.error('Token generation failed');
            }
        } else {
            // throw error when network is not connected
            this._toastService.error(
                'You are offline. Account Management is only available when connected to the internet.'
            );
        }
    }

    /**
     * Initialize Branch Web SDK for link creation
     */
    private _initializeWebSDK(): Promise<any> {
        if (this.initializationPromise) {
            return this.initializationPromise;
        }

        this.initializationPromise = new Promise((resolve, reject) => {
            // Check if Branch is already loaded
            if (window.branch) {
                this.branchInitialized = true;
                resolve(window.branch);
                return;
            }

            // Load Branch Web SDK
            const script = document.createElement('script');
            script.type = 'text/javascript';
            script.async = true;
            script.src = 'https://cdn.branch.io/branch-latest.min.js';

            script.onload = () => {
                console.log('Branch Web SDK loaded');

                // Initialize Branch with your key
                window.branch.init(
                    environment.branchKey,
                    {
                        metadata: {
                            source: 'capacitor_app'
                        }
                    },
                    (err: any, data: any) => {
                        if (err) {
                            console.error('Branch Web SDK initialization error:', err);
                            reject(err);
                        } else {
                            console.log('Branch Web SDK initialized successfully:', data);
                            this.branchInitialized = true;
                            resolve(window.branch);
                        }
                    }
                );
            };

            script.onerror = error => {
                console.error('Failed to load Branch Web SDK:', error);
                reject(error);
            };

            // Add script to document
            document.head.appendChild(script);
        });

        return this.initializationPromise;
    }

    /**
     * Create a Branch link with Open Graph tags
     */
    async createLinkWithOpenGraph(linkData: BranchLinkData): Promise<string> {
        try {
            // Ensure Web SDK is initialized
            if (!this.branchInitialized) {
                await this._initializeWebSDK();
            }

            return new Promise((resolve, reject) => {
                const branchLinkData = {
                    // Branch control parameters
                    feature: linkData.feature || 'sharing',
                    data: {
                        $og_title: linkData.title,
                        $og_description: linkData.description,
                        $og_image_url: linkData.imageUrl,
                        // $og_video: linkData.canonicalUrl || '',
                        $og_type: 'article',
                        // Fallback URLs
                        $desktop_url: linkData.url, // Fallback URL for desktop
                        custom_string: linkData.appRoute, // Route to navigate to in the app
                        custom_bool: linkData.isHorseProfileRoute // Is this a HorseProfile link?
                    }
                };

                console.log('Creating Branch link with data:', branchLinkData);

                window.branch.link(branchLinkData, (err: any, link: string) => {
                    if (err) {
                        console.error('Error creating Branch link:', err);
                        reject(err);
                    } else {
                        console.log('Branch link created successfully:', link);
                        resolve(link);
                    }
                });
            });
        } catch (error) {
            console.error('Error in createLinkWithOpenGraph:', error);
            throw error;
        }
    }
}
